import React, {useEffect, useState, useTransition} from 'react';
import {
    AppInterface,
    EventReminderButton,
    EventReminderPanel,
    EventTypeToggle,
    InternalPageHeaderBar,
    InvalidPanel,
    RecordsPagination,
    ThemedButton,
    ThemedConfirmDialog,
    ThemedIconButton,
    Wm_AddEdit_Page,
    Wm_InternalPage
} from "../../index";
import {EventTypes, GenRouteUrls, Keys, ModuleFieldsData, Modules, ReqUrls, ShowViewType} from "../../config/AppConfig";
import {useTheme} from "../../../common/ProjectThemeProvider";
import {
    checkNullArr,
    checkNullJson,
    checkNullStr,
    getArrLen,
    getHighlightedMatchedText,
    getJsonValue,
    getJsonValueFromNestedKeys,
    isBoolTrue,
    isDateExpired,
    isJsonValueTrue,
    limitStringWords,
    shuffleList,
    sortJsonListAdvance
} from "../../../common/fusion-ui/utils/ReactUtils";
import {formatDateValue, getAllocatedModuleName, getSubdomainSessionField, isCurrUserRoleAdmin, printError, printLog, sendRequest} from "../../utils/AppUtils";
import {useToast} from "../../../common/fusion-ui/provider/ToastProvider";
import {Anchor, Flexbox, IMsgButton, Label, NullMsgLabel, Section} from "../../../common/fusion-ui"
import {CssVariant, DefStrNA, Messages} from 'common/fusion-ui/config/FusionConfig';
import {useReduxPersistSessionData} from "../../hook/useReduxPersistSessionData";
import {useConfig} from "../../context/ConfigProvider";
import useScreenSize from "../../../common/fusion-ui/hook/useScreenSize";
import {Tooltip} from "@mui/material";
import {AnimationType, FramerAnimator} from "../../../common/fusion-ui/animation/FramerAnimator";

const WebMeetingPage = React.memo(({
                                       fromSearchPage = false,
                                       recordList = [],
                                       loadingStatus = {},
                                       showControls = true,
                                       showHeader = true,
                                       searchString,
                                   }) => {
    const TAG = "WebMeetingPage";

    const moduleName = Modules.WebMeeting;
    const mf = ModuleFieldsData[moduleName];

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();
    const {isConfigTrue, updateConfig, getConfig} = useConfig();
    const {isScreenUpSm, isScreenXs, isScreenSm} = useScreenSize();

    const animator = new FramerAnimator();

    const SEARCH_BY_PARAMS = {
        anything: {name: 'Anything'},
        title: {name: mf.title.titleAlt},
        outline: {name: mf.outline.titleAlt},
        presenter: {name: mf.presenter.titleAlt},
        date: {name: mf.date.titleAlt},
    };
    const SORT_BY_PARAMS = {
        title: {
            name: mf.title.titleAlt,
            sortKey: mf.title.column,
        },
        outline: {
            name: mf.outline.titleAlt,
            sortKey: mf.outline.column,
        },
        presenter: {
            name: mf.presenter.titleAlt,
            sortKey: mf.presenter.column,
        },
        date: {
            name: mf.date.titleAlt,
            sortKey: mf.date.column,
        },
    };
    const PAGINATION_RECORD_LIMITS = [8, 12, 16, 20];
    const DEF_PGNTN_RECORD_LIMIT = PAGINATION_RECORD_LIMITS[0];
    const DEF_PGNTN_START_INDEX = 1;
    const OUTLINE_WORD_LIMIT = 40;

    const Fields = {
        recordList: `${TAG}_recordList`,
        summaryList: `${TAG}_summaryList`,

        isDataLoading: 'isDataLoading',
        isDataFound: 'isDataFound',
        dataLoadingMsg: 'dataLoadingMsg',
        isDataFetched: 'isDataFetched',

        recordDeleteBtn_Disable: 'recordDeleteBtn_Disable',
        recordDeleteBtn_AsLoading: 'recordDeleteBtn_AsLoading',
        currRecordDeleteId: 'currRecordDeleteId',
        showDeleteDialog: 'showDeleteDialog',

        currEventTypeId: 'currEventTypeId',

        showInternalPopup: 'showInternalPopup',
        showEditFormPopup: 'showEditFormPopup',
        currRecordId_internal: 'currRecordId_internal',
        currRecordId_editForm: 'currRecordId_editForm',

        openEventReminderPopup: 'openEventReminderPopup',
        recordId_eventReminder: 'recordId_eventReminder',

        searchInputValue: 'searchInputValue',
        searchBySelectedOption: 'searchBySelectedOption',
        sortBySelectedOption: 'sortBySelectedOption',
        isSortByOrderAsc: 'isSortByOrderAsc',

        pagination_currPageNum: 'pagination_currPageNum',
        pagination_currPageLimit: 'pagination_currPageLimit',

        isSearchedStringSet: 'isSearchedStringSet',
    };
    const [fieldsData, setFieldsData] = useState({
        [Fields.isDataLoading]: true,
    });

    const {sessionData, updateSessionStoreFieldValue} = useReduxPersistSessionData({
        keys: [Fields.recordList, Fields.summaryList,],
    });

    const [sortListPending, startSortListTransition] = useTransition();
    const [searchListPending, startSearchListTransition] = useTransition();

    useEffect(() => {
        if (!isBoolTrue(fromSearchPage)) {
            // updateConfig(Keys.config_ShowTabBar, true);
            // updateConfig(Keys.config_TabBarTab, moduleName);
            // updateConfig(Keys.tracker_currPageModule, moduleName);
            // updateConfig(Keys.tracker_currPageTag, TAG);

            updateConfig({
                [Keys.config_ShowTabBar]: true,
                [Keys.config_TabBarTab]: moduleName,
                [Keys.tracker_currPageModule]: moduleName,
                [Keys.tracker_currPageTag]: TAG,
            });
        }
    }, [fromSearchPage]);

    useEffect(() => {
        //Initial def-values
        if (!checkNullStr(getField(Fields.pagination_currPageNum))) {
            updateField(Fields.searchBySelectedOption, SEARCH_BY_PARAMS.anything.name);
            updateField(Fields.sortBySelectedOption, SORT_BY_PARAMS.title.name);
            updateField(Fields.isSortByOrderAsc, true);
            updateField(Fields.pagination_currPageNum, DEF_PGNTN_START_INDEX);
            updateField(Fields.pagination_currPageLimit, DEF_PGNTN_RECORD_LIMIT);
            updateField(Fields.currEventTypeId, EventTypes.all.id);
        }

        if (isBoolTrue(fromSearchPage)) {
            if (!isFieldTrue(Fields.isDataFetched)) {
                updateField(Fields.isDataFetched, true);
                const success = checkNullArr(recordList);
                updateField(Fields.isDataLoading, false);
                updateField(Fields.isDataFound, success);
                updateField(Fields.dataLoadingMsg, success ? '' : Messages.noDataFound);

                if (success) {
                    updateSessionStoreFieldValue(Fields.recordList, recordList);
                }
            }
        } else {
            fetchPageData();
        }
    }, [sessionData, recordList, loadingStatus, searchString, fromSearchPage]);

    return (<>
        {isBoolTrue(fromSearchPage) ? createRecordListPanel() : createPage()}
        {isBoolTrue(showControls) && createDeleteDialog()}
        {(isCurrUserRoleAdmin()) && createEventReminderPopup()}
        {createInternalPanelPopup()}
        {isCurrUserRoleAdmin() && createEditFormPopup()}
    </>);

    function createPage() {
        return <>
            <AppInterface>
                <div className={'position-relative'}>
                    <InvalidPanel
                        color={theme.text70}
                        success={getField(Fields.isDataFound)}
                        loading={getField(Fields.isDataLoading)}
                        msg={getField(Fields.dataLoadingMsg)}
                        show404Image={true}
                    />

                    {(!isFieldTrue(Fields.isDataLoading) && isFieldTrue(Fields.isDataFound)) && <div className={`m-0 p-0 w-100`}>
                        {/*header-bar*/}
                        {animator.setAnimationType(AnimationType.SLIDE_DOWN).render(
                            <Flexbox className={'m-0 p-0  mb-2'}
                                     justifyAt={"between"} alignAt={"center"}>
                                {/*create-meeting btn*/}
                                {isCurrUserRoleAdmin() && <ThemedButton
                                    className={'fs-sm fw-semi-bold text-nowrap'}
                                    outlined={true}
                                    sleek={true}
                                    mb={1}
                                    mt={1}
                                    mr={1}
                                    // height={'28px'}
                                    text={`Create Meeting`}
                                    href={GenRouteUrls.wm_uploadPage}
                                    variant={theme.primaryBlue}
                                />}

                                {/*right*/}
                                <Flexbox className={''} justifyAt={"end"} alignAt={"center"}>
                                    {/*help btn*/}
                                    <IMsgButton className={'m-1 rounded-10'}
                                                tooltip={<div className={'text-start'}>{getSubdomainSessionField('get_help')}</div>}>
                                        <ThemedButton
                                            className={'fs-sm fw-semi-bold text-nowrap'}
                                            outlined={true}
                                            sleek={true}
                                            text={'Get Help'}
                                            variant={theme.primaryBlue}
                                        />
                                    </IMsgButton>
                                </Flexbox>
                            </Flexbox>
                        )}

                        {createRecordListPanel()}

                        {/* Summary list */}
                        {<div className={'mt-3 mb-2 w-100'}>
                            <Label
                                className={'m-0 p-0 px-1 fs-lg fw-bold'}
                                color={theme.primary}
                                textAt={'start'}>
                                {getAllocatedModuleName(moduleName)} Summary
                            </Label>

                            {/*links*/}
                            {checkNullArr(getSummaryList())
                                ? <div>
                                    {getSummaryList().map((item, index) => {
                                        return (checkNullStr(getJsonValue(item, 'name'))
                                                && checkNullStr(getJsonValue(item, 'link'))) &&
                                            <div key={index} className={"border rounded-3 p-1 mb-2"}
                                                 onMouseEnter={(e) => {
                                                     e.target.style.boxShadow = theme.boxShadowXs;
                                                 }}
                                                 onMouseLeave={(e) => {
                                                     e.target.style.boxShadow = 'none';
                                                 }}>
                                                <Anchor
                                                    className={'fw-semi-boldx m-0 px-1'}
                                                    textAt={'start'}
                                                    href={getJsonValue(item, 'link')}
                                                    target={'_blank'}
                                                    simple={true}>
                                                    {limitStringWords(getJsonValue(item, 'name'), 30, " ", '...')}
                                                </Anchor>
                                            </div>
                                    })}
                                </div>
                                : <NullMsgLabel className={'fw-semi-bold'} color={theme.text70} textAt={'start'}>Summary data not available</NullMsgLabel>}
                        </div>}
                    </div>}
                </div>
            </AppInterface>
        </>
    }

    function createRecordListPanel() {
        return <div>
            <div className={''}>
                {/*header-bar*/}
                <div>
                    {isBoolTrue(showHeader) && <InternalPageHeaderBar
                        moduleName={moduleName}
                        pos={''}
                        justifyAt={'between'}
                        bg={'transparent'}
                        px={0}
                        py={0}
                        pt={0}

                        searchByParams={SEARCH_BY_PARAMS}
                        sortByParams={SORT_BY_PARAMS}
                        showSearch={!isBoolTrue(fromSearchPage)}

                        recordsLen={getArrLen(getRecordList(true))}
                        filteredRecordsLen={getArrLen(getRecordList(true, true))}
                        searchValue={getField(Fields.searchInputValue)}
                        searchByDefOption={getField(Fields.searchBySelectedOption)}
                        sortByDefOption={getField(Fields.sortBySelectedOption)}
                        isSorting={sortListPending}
                        isSearching={searchListPending}
                        isSortAsc={isFieldTrue(Fields.isSortByOrderAsc)}

                        onSearchChange={(e, value) => handleSearchInputChange(value)}
                        onSearchBy={(name, item, index) => {
                            updateField(Fields.searchBySelectedOption, name);
                            handleSearchInputChange(getField(Fields.searchInputValue), name);
                        }}
                        onSort={(name, item, index, sortKey) => {
                            handleSortByChange(name, sortKey);
                        }}

                        leftComponent={<EventTypeToggle onSwitch={(id) => updateField(Fields.currEventTypeId, id)}/>}
                    />}
                </div>

                {/* record-list */}
                {animator.setAnimationType(AnimationType.SLIDE_UP).renderChildren(
                    <div className={'m-0 p-0 mt-1'}>
                        {checkNullArr(getRecordListToRender())
                            ? getRecordListToRender().map((item, index) => {
                                const recordId = getJsonValue(item, mf.recordId.column);
                                const eventStatusIndicator = getEventStatusIndicatorTag(item);

                                return (checkNullJson(item)) && <div
                                    key={index + getJsonValue(item, mf.title.column)}
                                    className={``}>
                                    <Section
                                        className={'m-0 p-2 h-auto mb-3 app-section hover'}
                                        position={'relative'}
                                        borderless={false}
                                        roundedVariant={4}
                                        borderColor={theme.text20}
                                        contentClassName={'px-1 h-100'}>
                                        <div>
                                            {/*title-link*/}
                                            <Flexbox className={''} justifyAt={'between'} alignAt={'start'}>
                                                {/*title*/}
                                                <div className={'d-flex w-auto'}>
                                                    <Anchor className={'fw-semi-bold text-wrap text-break'}
                                                            href={`${GenRouteUrls.wm_internalPage}${recordId}`}
                                                            onLinkClick={(e) => {
                                                                if (!isScreenUpSm) {
                                                                    e.preventDefault();
                                                                    if (e.ctrlKey) {
                                                                        window.open(`${GenRouteUrls.wm_internalPage}${recordId}`, '_blank');
                                                                    } else {
                                                                        updateField(Fields.currRecordId_internal, recordId);
                                                                        updateField(Fields.showInternalPopup, true);
                                                                    }
                                                                }
                                                            }}>
                                                        {highlightSearchMatchedText((getJsonValue(item, mf.title.column)), SEARCH_BY_PARAMS.title.name)}
                                                    </Anchor>
                                                </div>

                                                {/*event-status tag*/}
                                                {checkNullStr(eventStatusIndicator.label) &&
                                                    <Label className={'ms-1 px-2 py-0 select-none d-flex al-center fs-md-sm fw-bold rounded-10'}
                                                           bgColor={eventStatusIndicator.bg}
                                                           color={eventStatusIndicator.color}
                                                           height={'20px'}>
                                                        {eventStatusIndicator.label}
                                                        {/*<span> | {getJsonValue(item, mf.date.column)}</span>*/}
                                                    </Label>}
                                            </Flexbox>

                                            {/*outline*/}
                                            <div>
                                                <Label className={'m-0 p-0 text-wrap'} color={theme.text} textAt={'start'}>
                                                <span>{" "}
                                                    {highlightSearchMatchedText(limitStringWords(getJsonValue(item, mf.outline.column, DefStrNA), OUTLINE_WORD_LIMIT, " ", '...'), SEARCH_BY_PARAMS.outline.name)}
                                                </span>
                                                </Label>
                                            </div>

                                            {/*presenter*/}
                                            <div>
                                                <Label className={'m-0 p-0 text-wrap'} color={theme.text} textAt={'start'}>
                                                    <span className={'fw-semi-bold'}>{mf.presenter.titleAlt}: </span>
                                                    <span>{" "}
                                                        {highlightSearchMatchedText((getJsonValue(item, mf.presenter.column, DefStrNA)), SEARCH_BY_PARAMS.presenter.name)}
                                                </span>
                                                </Label>
                                            </div>

                                            {/*date*/}
                                            <div>
                                                <Label className={'m-0 p-0 text-wrap'} color={theme.text} textAt={'start'}>
                                                    <span className={'fw-semi-bold'}>{mf.date.titleAlt}: </span>
                                                    <span className={'text-nowrap'}>{" "}
                                                        {highlightSearchMatchedText(formatDateValue(getJsonValue(item, mf.date.column, DefStrNA)), SEARCH_BY_PARAMS.date.name)}
                                                </span>
                                                </Label>
                                            </div>

                                            {/*controls*/}
                                            {isBoolTrue(showControls) &&
                                                <Flexbox className={''}
                                                         position={isScreenUpSm ? '' : 'absolute'}
                                                         justifyAt={'start'} alignAt={'center'}
                                                         style={{bottom: '.4rem', right: '.4rem'}}>
                                                    {/* reminder send-btn */}
                                                    <Tooltip title={isEventExpired(item) ? `Reminder can't be sent for a past event.` : ''}>
                                                        <div>
                                                            {isCurrUserRoleAdmin() && <EventReminderButton
                                                                circular={true}
                                                                m={1} mr={2}
                                                                size={'26px'} iconSize={'16px'}
                                                                disabled={isEventExpired(item)}
                                                                onClick={() => {
                                                                    updateField(Fields.openEventReminderPopup, true);
                                                                    updateField(Fields.recordId_eventReminder, recordId);
                                                                }}
                                                            />}
                                                        </div>
                                                    </Tooltip>


                                                    {/* edit-btn */}
                                                    {isCurrUserRoleAdmin() && <ThemedIconButton
                                                        className={''}
                                                        tooltip={'Edit'}
                                                        m={1}
                                                        color={theme.primaryBlue}
                                                        bgColor={theme.textWhite}
                                                        hoverBgColor={theme.primaryBlue}
                                                        borderColor={theme.primaryBlue}
                                                        outlined={true}
                                                        icon={'bi bi bi-pencil'}
                                                        iconSize={'11px'}
                                                        size={'26px'}
                                                        href={`${GenRouteUrls.wm_editPage}${recordId}`}
                                                        onClick={(e) => {
                                                            if (!isScreenUpSm) {
                                                                e.preventDefault();
                                                                if (e.ctrlKey) {
                                                                    window.open(`${GenRouteUrls.wm_editPage}${recordId}`, '_blank');
                                                                } else {
                                                                    updateField(Fields.currRecordId_editForm, recordId);
                                                                    updateField(Fields.showEditFormPopup, true);
                                                                }
                                                            }
                                                        }}
                                                    />}

                                                    {/* delete-btn */}
                                                    {isCurrUserRoleAdmin() && <ThemedIconButton
                                                        className={''}
                                                        tooltip={'Delete'}
                                                        m={1}
                                                        ml={2}
                                                        variant={'red'}
                                                        bgColor={theme.textWhite}
                                                        hoverBgColor={'red'}
                                                        outlined={true}
                                                        icon={'bi bi bi-trash'}
                                                        iconSize={'11px'}
                                                        size={'26px'}
                                                        loadingVariant={CssVariant.danger}
                                                        asLoading={isFieldTrue(Fields.recordDeleteBtn_AsLoading) && getField(Fields.currRecordDeleteId) == recordId}
                                                        disabled={isFieldTrue(Fields.recordDeleteBtn_Disable) && getField(Fields.currRecordDeleteId) == recordId}
                                                        onClick={() => {
                                                            updateField(Fields.currRecordDeleteId, recordId);
                                                            updateField(Fields.showDeleteDialog, true);
                                                        }}
                                                    />}
                                                </Flexbox>}
                                        </div>
                                    </Section>
                                </div>
                            })
                            : <NullMsgLabel className={'fw-semi-bold'} color={theme.text70} textAt={'start'}>
                                {isSearchOngoing() ? 'Search not found!' : 'No records found!'}
                            </NullMsgLabel>}
                    </div>
                )}

                {/* pagination */}
                <RecordsPagination
                    id={TAG}
                    totalRecords={getArrLen(getRecordList(true, true))}
                    totalRecordsToRender={getArrLen(getRecordListToRender())}
                    recordLimits={PAGINATION_RECORD_LIMITS}
                    onPageChange={(e, newPageIndex) => {
                        // log('RecordsPagination.onLimitChange.newPageIndex:', newPageIndex);
                        updateField(Fields.pagination_currPageNum, newPageIndex);
                    }}
                    onLimitChange={(newLimit, newPageIndex) => {
                        // log('RecordsPagination.onLimitChange.newLimit:', newLimit, 'newPageIndex:', newPageIndex);
                        updateField(Fields.pagination_currPageLimit, newLimit);
                        updateField(Fields.pagination_currPageNum, newPageIndex);
                    }}
                />
            </div>
        </div>
    }

    function createEventReminderPopup() {
        return <EventReminderPanel
            moduleName={moduleName}
            recordId={getField(Fields.recordId_eventReminder)}
            open={isFieldTrue(Fields.openEventReminderPopup)}
            recordUrl={`${GenRouteUrls.wm_internalPage}${getField(Fields.recordId_eventReminder)}`}
            onDoneAndHide={() => {
                updateField(Fields.openEventReminderPopup, false);
                updateField(Fields.recordId_eventReminder, null);
            }}
        />
    }

    function createInternalPanelPopup() {
        return <Wm_InternalPage
            showAs={ShowViewType.DRAWER}
            recordId={getField(Fields.currRecordId_internal)}
            open={isFieldTrue(Fields.showInternalPopup)}
            onHide={() => {
                updateField(Fields.showInternalPopup, false);
                updateField(Fields.currRecordId_internal, null);
            }}
        />
    }

    function createEditFormPopup() {
        return <Wm_AddEdit_Page
            showAs={ShowViewType.DRAWER}
            updateOnly={true}
            recordId={getField(Fields.currRecordId_editForm)}
            open={isFieldTrue(Fields.showEditFormPopup)}
            onSuccess={() => {
                updateField(Fields.showEditFormPopup, false);
                updateField(Fields.currRecordId_editForm, null);
                fetchPageData(false);
            }}
            onHide={() => {
                updateField(Fields.showEditFormPopup, false);
                updateField(Fields.currRecordId_editForm, null);
            }}
        />
    }

    function createDeleteDialog() {
        return <ThemedConfirmDialog
            open={isFieldTrue(Fields.showDeleteDialog)}
            onCancel={() => updateField(Fields.showDeleteDialog, false)}
            onHide={() => updateField(Fields.currRecordDeleteId, null)}
            title={`Delete ${moduleName}`}
            subMsg={getJsonValue(getRecordList().find(item => getField(Fields.currRecordDeleteId) == getJsonValue(item, mf.recordId.column)), mf.title.column)}
            operation={'delete'}
            target={'this web-meeting'}
            onConfirm={() => {
                handleRecordDelete();
            }}
        />
    }

    async function fetchPageData(checkBefore = true) {
        const fun = 'fetchPageData:';
        if (!checkBefore || (checkBefore && !isFieldTrue(Fields.isDataFetched))) {
            updateField(Fields.isDataFetched, true);
            try {
                updateField(Fields.isDataFound, false);
                updateField(Fields.isDataLoading, true);

                const sessionDataFound = checkBefore ? checkNullArr(getJsonValue(sessionData, Fields.recordList)) : false;
                log(fun, 'sessionDataFound:', sessionDataFound, 'fromSearchPage:', fromSearchPage);
                if (sessionDataFound) {
                    updateField(Fields.isDataFound, true);
                    updateField(Fields.isDataLoading, false);
                } else {
                    const REQUEST_OPTIONS = {};
                    sendRequest({
                        reqUrl: ReqUrls.wm__mainPage_fetch_recordList,
                        reqOptions: REQUEST_OPTIONS,
                        onFetched: ((fetchedData) => {
                            log(fun, 'fetchedData:', fetchedData);

                            const recordList = getJsonValueFromNestedKeys(fetchedData, ['data', 'recordList'], []);
                            const summaryList = getJsonValueFromNestedKeys(fetchedData, ['data', 'summaryList'], []);

                            const success = checkNullArr(recordList);
                            updateField(Fields.isDataFound, success);
                            if (success) {
                                // toast(Messages.fetchSuccess, CssVariant.success);success)

                                //Sort list by title by-default
                                // sortJsonListAdvance(recordList, SORT_BY_PARAMS.title.sortKey);

                                updateSessionStoreFieldValue(Fields.recordList, recordList);
                                updateSessionStoreFieldValue(Fields.summaryList, summaryList);
                            } else {
                                updateField(Fields.dataLoadingMsg, Messages.noDataFound);
                                toast(Messages.noDataFound, CssVariant.danger);
                            }
                            updateField(Fields.isDataLoading, false);
                        }),
                        onError: ((error) => {
                            logErr(fun, error);
                            updateField(Fields.isDataLoading, false);
                            updateField(Fields.isDataFound, false);
                            updateField(Fields.dataLoadingMsg, Messages.fetchError);
                        }),
                    });
                }
            } catch (error) {
                logErr(fun, error);
                updateField(Fields.isDataLoading, false);
                updateField(Fields.isDataFound, false);
                updateField(Fields.dataLoadingMsg, Messages.fetchError);
            }
        }
    }

    function handleRecordDelete() {
        const fun = 'handleRecordDelete';
        const recordId = getField(Fields.currRecordDeleteId);
        if (checkNullStr(recordId)) {
            updateField(Fields.recordDeleteBtn_Disable, true);
            updateField(Fields.recordDeleteBtn_AsLoading, true);
            try {
                const REQUEST_OPTIONS = {recordId: recordId};
                sendRequest({
                    reqUrl: ReqUrls.wm__mainPage_delete_record,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);
                        updateField(Fields.showDeleteDialog, false)

                        const success = isJsonValueTrue(fetchedData, 'success');
                        const message = getJsonValue(fetchedData, 'message');
                        if (success) {
                            toast(message, CssVariant.success);
                            // log(fun, 'getRecordList-old.before:', getArrLen(getRecordList()));


                            //Remove this recordId item from recordList
                            const recordList = getRecordList().filter((item) => recordId != getJsonValue(item, mf.recordId.column));
                            updateSessionStoreFieldValue(Fields.recordList, recordList);
                            // log(fun, 'getRecordList-after.length:', getArrLen(recordList));

                            if (!checkNullArr(recordList)) {
                                updateField(Fields.isDataFound, false);
                                updateField(Fields.dataLoadingMsg, "No records found!");
                            }
                        } else {
                            toast(message, CssVariant.danger);
                        }
                        updateField(Fields.currRecordDeleteId, null);
                        updateField(Fields.recordDeleteBtn_Disable, false);
                        updateField(Fields.recordDeleteBtn_AsLoading, false);
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                        updateField(Fields.recordDeleteBtn_Disable, false);
                        updateField(Fields.recordDeleteBtn_AsLoading, false);
                    }),
                });
            } catch (error) {
                logErr(fun, error);
                updateField(Fields.recordDeleteBtn_Disable, false);
                updateField(Fields.recordDeleteBtn_AsLoading, false);
            }
        } else {
            toast('Record-id not found!', CssVariant.danger);
        }
    }

    function handleSearchInputChange(inputValue, searchByOption = getField(Fields.searchBySelectedOption), recordList) {
        const fun = 'handleSearchInputChange:';
        log(fun, 'inputValue:', inputValue, 'searchByOption:', searchByOption, 'isSearchedStringSet:', isFieldTrue(Fields.isSearchedStringSet));
        startSearchListTransition(() => {
            performOps();
        });

        function performOps() {
            try {
                updateField(Fields.searchInputValue, inputValue);

                // Filter search query based on selected search filter
                const inputValueL = (inputValue + '').toLowerCase();
                recordList = checkNullArr(recordList) ? recordList : getRecordList();
                if (checkNullStr(inputValue) && checkNullArr(recordList)) {
                    recordList = recordList.map((item) => {
                        const title = (getJsonValue(item, mf.title.column) + '').toLowerCase();
                        const outline = (getJsonValue(item, mf.outline.column) + '').toLowerCase();
                        const presenter = (getJsonValue(item, mf.presenter.column) + '').toLowerCase();
                        const date = (formatDateValue(getJsonValue(item, mf.date.column)) + '').toLowerCase();

                        let matched = false;
                        switch (searchByOption) {
                            case SEARCH_BY_PARAMS.anything.name:
                                matched = (title + outline + presenter + date).includes(inputValueL);
                                break;
                            case SEARCH_BY_PARAMS.title.name:
                                matched = title.includes(inputValueL);
                                break;
                            case SEARCH_BY_PARAMS.outline.name:
                                matched = outline.includes(inputValueL);
                                break;
                            case SEARCH_BY_PARAMS.presenter.name:
                                matched = presenter.includes(inputValueL);
                                break;
                            case SEARCH_BY_PARAMS.date.name:
                                matched = date.includes(inputValueL);
                                break;
                        }
                        return {
                            ...item,
                            searched: matched
                        }
                    });
                }

                // update-list
                updateSessionStoreFieldValue(Fields.recordList, recordList);
            } catch (error) {
                logErr(fun, error);
                toast('Search failed, please try again', CssVariant.danger);
            }
        }
    }

    function handleSortByChange(name, sortKey) {
        const fun = 'handleSortByChange:';
        try {
            startSortListTransition(() => {
                const sortOption = getField(Fields.sortBySelectedOption);
                const order = name == sortOption ? !isFieldTrue(Fields.isSortByOrderAsc) : isFieldTrue(Fields.isSortByOrderAsc);

                updateField(Fields.isSortByOrderAsc, order);
                updateField(Fields.sortBySelectedOption, name);

                updateSessionStoreFieldValue(Fields.recordList, sortJsonListAdvance(shuffleList(getRecordList()), sortKey, order));
            });
        } catch (error) {
            toast('Sort failed, please try again', CssVariant.danger);
            logErr(fun, error);
        }
    }

    function highlightSearchMatchedText(text, searchByParam) {
        const can = (getField(Fields.searchBySelectedOption) == SEARCH_BY_PARAMS.anything.name || getField(Fields.searchBySelectedOption) == searchByParam);
        return can ? getHighlightedMatchedText(text, getField(Fields.searchInputValue)) : text;
    }


    function getRecordList(categorized = false, searchedOnly = false) {
        if (isBoolTrue(searchedOnly) && isSearchOngoing()) {
            return getList().filter(item => isJsonValueTrue(item, 'searched'))
        } else {
            return getList();
        }

        function getList() {
            if (checkNullArr(getJsonValue(sessionData, Fields.recordList))) {
                if (isBoolTrue(categorized) && getField(Fields.currEventTypeId) != EventTypes.all.id) {
                    return getJsonValue(sessionData, Fields.recordList)
                        // .filter(item => (getField(Fields.currEventTypeId) == EventTypes.expired.id ? isEventExpired(item) : !isEventExpired(item)));
                        .filter(item => {
                            const statusLabel = getEventStatusIndicatorTag(item).label;
                            switch (getField(Fields.currEventTypeId)) {
                                // case  EventTypes.ongoing.id:
                                //     return statusLabel == EventTypes.ongoing.text;
                                // case  EventTypes.today.id:
                                //     return statusLabel == EventTypes.today.text;
                                // case  EventTypes.tomorrow.id:
                                //     return statusLabel == EventTypes.tomorrow.text;
                                case  EventTypes.upcoming.id:
                                    return statusLabel == EventTypes.upcoming.text;
                                case  EventTypes.expired.id:
                                    return statusLabel == EventTypes.expired.text || statusLabel == EventTypes.expired.textAlt || statusLabel == EventTypes.expired.textAlt2;
                                default:
                                    return false;
                            }
                            // return getField(Fields.currEventTypeId) == EventTypes.expired.id ? isEventExpired(item) : !isEventExpired(item)
                        });
                } else {
                    return getJsonValue(sessionData, Fields.recordList);
                }
            }
            return [];
        }
    }

    function getSummaryList() {
        return checkNullArr(getJsonValue(sessionData, Fields.summaryList)) ? getJsonValue(sessionData, Fields.summaryList) : [];
        // return checkNullArr(getField(Fields.summaryList)) ? getField(Fields.summaryList) : [];
    }


    function getRecordListToRender(categorized = true) {
        return getRecordList(categorized, true).slice((getField(Fields.pagination_currPageNum) - 1) * getField(Fields.pagination_currPageLimit), getField(Fields.pagination_currPageNum) * getField(Fields.pagination_currPageLimit));
    }

    function isSearchOngoing() {
        return checkNullStr(getField(Fields.searchInputValue));
    }

    function isEventExpired(record = {}) {
        return isDateExpired(getJsonValue(record, mf.date.column));
    }

    /**
     * Checks the status of event-date & return appropriate status-label/status-indicator like ongoing/today/ended/expired etc.
     * @param record{Object} - Object from which date is to be extracted.
     * @return {Object} - Object containing "tag", "bg" and "color" keys with their values.
     */
    function getEventStatusIndicatorTag(record) {
        const date = getJsonValue(record, mf.date.column);
        if (isDateExpired(date)) {
            return {label: EventTypes.expired.textAlt, bg: 'var(--danger15)', color: 'var(--danger)'};
            // } else if (isDateInCurrentPeriod(date, 'hour')) {
            //     return {label: EventTypes.ongoing.text, bg: theme.primaryAlt10, color: theme.primaryAlt};
            // } else if (isDateTomorrow(date)) {
            // return {label: EventTypes.tomorrow.text, bg: theme.primaryDark10, color: theme.primaryDark};
            // } else if (isDateInCurrentPeriod(date, 'day')) {
            //     return {label: EventTypes.today.text, bg: theme.primaryAltDark10, color: theme.primaryAltDark};
        } else if (!isDateExpired(date)) {
            return {label: EventTypes.upcoming.text, bg: theme.primaryBlue10, color: theme.primaryBlue};
        } else {
            return {label: "", bg: 'var(--danger20)', color: 'var(--danger)'};
        }
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
        log(...[text]);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }

});
export default WebMeetingPage;
